import React, { useState, useEffect } from 'react';
import { Platform, Linking } from 'react-native';
import { MemberDetails } from '~/models/MemberDetails';
import { HStack } from '~/components/gluestack/hstack';
import { EventAttendButton } from './EventAttendButton';
import { EventFollowButton } from './EventFollowButton';
import { ButtonWrapper } from '~/components/ui/ButtonWrapper';
import { ShareModal } from '~/components/ui/ShareModal';
import { useTranslation } from 'react-i18next';
import { useShare } from '~/hooks/useShare';
import { EventsService } from '~/services/domain/EventsService';
import { CalendarDownload01Icon, Share05Icon } from '@hugeicons-pro/core-stroke-rounded';

interface EventActionsProps {
  member: MemberDetails;
  className?: string;
}

export function EventActions({ member, className }: EventActionsProps) {
  const eventInfo = member.EventDetails;
  const { t } = useTranslation();
  const { handleShare, handleCopyUrl, handleOpenUrl, showShareModal, setShowShareModal, shareUrl } =
    useShare();
  const [isAttending, setIsAttending] = useState(eventInfo?.IsAttending ?? false);
  const [isFollowing, setIsFollowing] = useState(eventInfo?.IsFollowing ?? false);

  useEffect(() => {
    setIsAttending(eventInfo?.IsAttending ?? false);
  }, [eventInfo?.IsAttending]);

  useEffect(() => {
    setIsFollowing(eventInfo?.IsFollowing ?? false);
  }, [eventInfo?.IsFollowing]);

  const handleAttendingChange = (attending: boolean) => {
    setIsAttending(attending);
    if (!attending) {
      setIsFollowing(true);
    }
  };

  if (!eventInfo) return null;

  const handleShareEvent = () => {
    handleShare({
      type: 'event',
      userName: member.UserName,
      title: eventInfo.Name,
    });
  };

  const handleAddToCalendar = () => {
    const url = EventsService.getICalUrl(member.UserName);
    if (Platform.OS === 'web') {
      window.open(url, '_blank');
    } else {
      Linking.openURL(url);
    }
  };

  return (
    <>
      <HStack className={`flex-row flex-nowrap shrink-0 items-center gap-3 ${className || ''}`}>
        <EventAttendButton
          eventId={member.Id}
          eventName={eventInfo.Name}
          isAttending={eventInfo.IsAttending}
          userName={member.UserName}
          onAttendingChange={handleAttendingChange}
        />
        {!isAttending && (
          <EventFollowButton
            eventId={member.Id}
            isFollowing={isFollowing}
            userName={member.UserName}
            onFollowingChange={setIsFollowing}
          />
        )}
        <ButtonWrapper
          icon={CalendarDownload01Icon}
          size="sm"
          action="primary"
          variant="solid"
          accessibilityLabel={t('events.addToCalendar')}
          onPress={handleAddToCalendar}
        />
        <ButtonWrapper
          icon={Share05Icon}
          size="sm"
          action="primary"
          variant="solid"
          onPress={handleShareEvent}
        />
      </HStack>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={shareUrl}
        onCopyUrl={handleCopyUrl}
        onOpenUrl={handleOpenUrl}
        title={eventInfo.Name}
      />
    </>
  );
}
